import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Calendar,
  Crown,
  Eye,
  Info,
  Mic,
  MoreVertical,
  PenSquare,
  Pencil,
  Share2,
  Shield,
  Users,
  ClipboardList,
} from 'lucide-react';
import { useState } from 'react';
import { formatDate } from '@utils/dateFormat';
import formatString from '@utils/formatString';
import { getStatusColor } from '@utils/colorUtils';

// Access level icons
const ACCESS_ICONS = {
  Owner: Crown,
  Admin: Shield,
  Editor: Pencil,
  Viewer: Eye,
};

/**
 * Compact progress bar for the card
 */
function CardProgressBar({ value, max, showInfinity = false }) {
  const theme = useTheme();
  const percentage = showInfinity ? Math.min(value * 3, 100) : max > 0 ? (value / max) * 100 : 0;

  const getColor = () => {
    if (percentage >= 90) return theme.palette.error.main;
    if (percentage >= 70) return theme.palette.warning.main;
    return theme.palette.primary.main;
  };

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <Box
        sx={{
          height: 4,
          borderRadius: 2,
          backgroundColor: alpha(getColor(), 0.12),
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${Math.min(percentage, 100)}%`,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${getColor()}, ${alpha(getColor(), 0.7)})`,
            transition: 'width 0.4s ease-out',
          }}
        />
      </Box>
    </Box>
  );
}

/**
 * Study Card Component - Beautiful card layout for studies
 */
export default function StudyCard({
  study,
  onEdit,
  onPreview,
  onShare,
  onInfo,
  onNavigate,
  onMoreActions,
  disabledState,
  getDisabledTooltip,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const isInterview = study.type === 'Interview';
  const isSurvey = study.type === 'Survey';
  const maxParticipants = study.max_participants || 0;
  const responses = study.response || study.total_responses || 0;
  const status = study.status;
  const endDate = study.end_date || study.expires_at;
  const accessType = study.user_access_type || 'Viewer';
  const AccessIcon = ACCESS_ICONS[accessType] || Eye;

  const statusColor = getStatusColor(status);
  const typeColor = isInterview ? theme.palette.info.main : theme.palette.success.main;

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        boxShadow: `0 1px 3px ${alpha(theme.palette.grey[500], 0.08)}`,
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.12)}`,
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${typeColor}, ${alpha(typeColor, 0.5)})`,
        },
      }}
      onClick={() => onNavigate?.(study)}
    >
      <CardContent sx={{ p: 1.5, pt: 2, '&:last-child': { pb: 1.5 } }}>
        {/* Header Row - Type icon, status, actions all in one row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {/* Type Icon */}
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.5,
                backgroundColor: alpha(typeColor, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isInterview ? (
                <Mic size={14} color={typeColor} />
              ) : (
                <ClipboardList size={14} color={typeColor} />
              )}
            </Box>
            {/* Status Dot */}
            <Tooltip title={formatString(status)} arrow placement="top">
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: statusColor,
                  boxShadow: `0 0 6px ${alpha(statusColor, 0.4)}`,
                }}
              />
            </Tooltip>
            {/* Access Indicator */}
            <Tooltip title={accessType} arrow placement="top">
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 0.75,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AccessIcon size={11} color={theme.palette.primary.main} />
              </Box>
            </Tooltip>
          </Box>

          {/* Quick Actions + Menu - all in header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: -0.5 }}>
            <Tooltip title={disabledState?.edit ? getDisabledTooltip?.(study, 'edit') : 'Edit'}>
              <span>
                <IconButton
                  size="small"
                  disabled={disabledState?.edit}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit?.(study);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <PenSquare size={14} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={disabledState?.preview ? getDisabledTooltip?.(study, 'preview') : 'Preview'}
            >
              <span>
                <IconButton
                  size="small"
                  disabled={disabledState?.preview}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview?.(study);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Eye size={14} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                disabledState?.shareWithParticipants
                  ? getDisabledTooltip?.(study, 'shareWithParticipants')
                  : 'Share'
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={disabledState?.shareWithParticipants}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare?.(study);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Share2 size={14} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onInfo?.(study);
                }}
                sx={{ p: 0.5 }}
              >
                <Info size={14} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={handleMenuOpen} sx={{ p: 0.5 }}>
              <MoreVertical size={14} />
            </IconButton>
          </Box>
        </Box>

        {/* Title */}
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.35,
            minHeight: 36,
            fontSize: '0.875rem',
          }}
        >
          {study.title}
        </Typography>

        {/* Tags Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Chip
            label={study.type}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
              backgroundColor: alpha(typeColor, 0.1),
              color: typeColor,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          {study.is_primary === false && (
            <Chip
              label="Secondary"
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
            />
          )}
        </Box>

        {/* Stats Row - more compact */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 0.75,
            px: 1,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.grey[500], 0.04),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Users size={12} color={theme.palette.text.secondary} />
            <Typography variant="caption" fontWeight={600}>
              {responses}
              <Typography component="span" variant="caption" color="text.secondary">
                /{isInterview && maxParticipants > 0 ? maxParticipants : '∞'}
              </Typography>
            </Typography>
          </Box>
          {endDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Calendar size={12} color={theme.palette.text.secondary} />
              <Typography
                variant="caption"
                fontSize="0.7rem"
                color={new Date(endDate) < new Date() ? 'error.main' : 'text.secondary'}
              >
                {formatDate(endDate)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Progress Bar */}
        <CardProgressBar
          value={responses}
          max={maxParticipants}
          showInfinity={isSurvey && maxParticipants !== 0}
        />
      </CardContent>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        elevation={3}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 160,
            mt: 0.5,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            onMoreActions?.(study, 'duplicate');
          }}
        >
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            onMoreActions?.(study, 'collaborators');
          }}
          disabled={disabledState?.addCollaborators}
        >
          Manage Collaborators
        </MenuItem>
        {status === 'active' && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onMoreActions?.(study, 'stop');
            }}
            disabled={disabledState?.stopCollectingResponses}
          >
            Stop Collecting
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleMenuClose();
            onMoreActions?.(study, 'archive');
          }}
          disabled={disabledState?.archive}
        >
          {status === 'archived' ? 'Unarchive' : 'Archive'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            onMoreActions?.(study, 'delete');
          }}
          disabled={disabledState?.delete}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Card>
  );
}
